import React, { useEffect, useState } from "react";
import CustomAlert from "../../helpers/CustomAlert";
import { toast } from "react-toastify";
import { apiList } from "../../helpers/apiList";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { CircularProgress } from "@mui/material";
import AddUserIcon from "../../assets/Icons/add-user-img 1.svg";
import LeftCircle from "../../assets/Icons/Left Circle.svg";
import RightCircle from "../../assets/Icons/Right Circle.svg";

const EntityModal = ({
  show,
  handleClose,
  customerCode,
  name,
  entityDetails,
  modalType,
  handleShowTable,
}) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const [entityId, setEntityId] = useState("");
  const [entityName, setEntityName] = useState("");
  const [entityIdError, setEntityIdError] = useState("");
  const [entityNameError, setEntityNameError] = useState("");
  const [loading, setLoading] = useState(false);

  const isReadOnly = !!entityDetails;

  const handleAddEntity = async (e) => {
    e.preventDefault();

    let isValid = true;
    // Validate Entity ID
    if (!entityId) {
      setEntityIdError("Entity ID is required");
      isValid = false;
    } else if (!/^[1-9][0-9]{5,18}$/.test(entityId)) {
      setEntityIdError(
        "Entity ID must be 6 to 19 digits and cannot start with 0"
      );
      isValid = false;
    } else if (!/^[0-9]+$/.test(entityId)) {
      setEntityIdError("Entity ID must contain digits only (0-9)");
      isValid = false;
    } else if (/^(\d)\1+$/.test(entityId)) {
      setEntityIdError("Entity ID cannot be the same digit repeated");
      isValid = false;
    } else {
      setEntityIdError("");
    }

    // Validate Entity Name
    if (!entityName) {
      setEntityNameError("Entity Name is required");
      isValid = false;
    } else if (entityName.length < 2 || entityName.length > 50) {
      setEntityNameError("Entity Name must be 2 to 50 characters long");
      isValid = false;
    } else if (!/^[A-Za-z0-9 ]+$/.test(entityName)) {
      setEntityNameError(
        "Entity Name can only contain letters, numbers, and spaces"
      );
      isValid = false;
    } else if (/^\s|\s$/.test(entityName)) {
      setEntityNameError("Entity Name cannot start or end with spaces");
      isValid = false;
    } else if (/\s{2,}/.test(entityName)) {
      setEntityNameError("Entity Name cannot have multiple consecutive spaces");
      isValid = false;
    } else if (/^(\d)\1+$/.test(entityName)) {
      setEntityNameError("Entity Name cannot be the same digit repeated");
      isValid = false;
    } else {
      setEntityNameError("");
    }

    if (!isValid) return;

    const data = {
      username: loggedInUserName,
      entityId: entityId,
      userCode: customerCode,
      entityName: entityName,
    };

    try {
      const response = await axiosPostApi(apiList.DLT_ADD_ENTITY, data);

      if (response?.status === 200 && response?.data?.status === true) {
        toast.success("Entity added successfully!", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        handleClose();
        handleBack();
      } else {
        CustomAlert({
          title: "Failed to Add Entity",
          text:
            response.data.message ||
            "There was an issue processing the request. Please try again.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      toast.error("Failed to add entity. Please try again.");
    }
  };

  const handleBack = () => {
    if (modalType !== "modify") {
      setEntityName("");
    }
    handleClose();
    setEntityId("");
    setEntityIdError("");
    setEntityNameError("");
  };

  const handleModifyData = async (entity) => {
    if (!entityName) {
      setEntityNameError("Entity Name is required");
      return;
    }

    setEntityIdError("");

    const data = {
      username: loggedInUserName,
      entityId: entityDetails?.id,
      entityName,
    };
    try {
      setLoading(true);
      const response = await axiosPostApi(apiList.MODIFY_DATA, data);
      if (response?.status === 200) {
        toast.success("Entity Name Update Successfully!", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        handleClose();
        handleShowTable();
        handleBack();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    modalType === "modify" ? handleModifyData() : handleAddEntity(e);
  };

  useEffect(() => {
    if (modalType === "modify") {
      setEntityName(entityDetails?.name);
    }
  }, [modalType, entityDetails?.name]);

  return (
    <div
      className={`modal fade ${show ? "show d-block" : ""}`}
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div className="modal-dialog modal-md" role="document">
        <div className="modal-content w-[469px] !rounded-[16px]">
          <img
            src={LeftCircle}
            alt="leftImg"
            className="absolute left-0 bottom-0 w-[110px] h-[105px]"
          />
          <img
            src={RightCircle}
            alt="leftImg"
            className="absolute right-0 top-0 w-[110px] h-[105px]"
          />
          <div className="modal-header border-b-0 flex items-center justify-between p-4 pb-2">
            <div className="flex items-center gap-2">
              <img src={AddUserIcon} alt="add-user" className="h-[24px] mr-2" />
              <h5 className="modal-title main-title m-0 font-medium">
                {modalType === "view"
                  ? "View Entity Details"
                  : modalType === "modify"
                  ? "Modify Entity Details"
                  : "Add Entity ID"}
              </h5>
            </div>
            <button
              type="button"
              className="btn-close cursor-pointer z-20"
              aria-label="Close"
              onClick={handleBack}
            ></button>
          </div>
          <form onSubmit={handleFormSubmit}>
            <div className="modal-body p-4 pb-0">
              <div className="mb-4 w-full">
                <label htmlFor="entityName" className="form-label block mb-1">
                  Entity Name<span style={{ color: "#DC3545" }}>*</span>
                </label>
                <input
                  type="text"
                  id="entityName"
                  className="form-control w-full p-2 border !border-[#495057] rounded"
                  placeholder="Enter Entity Name"
                  value={
                    modalType === "view"
                      ? entityDetails?.name
                      : modalType === "modify"
                      ? entityName
                      : entityName
                  }
                  onChange={(e) => setEntityName(e.target.value)}
                  autoComplete="off"
                  readOnly={modalType === "view"}
                />
                {entityNameError && modalType !== "view" && (
                  <div className="text-danger text-sm mt-1">
                    {entityNameError}
                  </div>
                )}
              </div>
              <div className="mb-4 w-full">
                <label htmlFor="entityId" className="form-label block mb-1">
                  Entity ID
                  <span style={{ color: "#DC3545" }}>*</span>
                </label>
                <input
                  type="text"
                  id="entityId"
                  className="form-control w-full p-2 border !border-[#495057] rounded"
                  placeholder="Enter Entity ID"
                  value={isReadOnly ? entityDetails?.id : entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                  readOnly={isReadOnly}
                  autoComplete="off"
                  style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                />
                {entityIdError && (
                  <div className="text-danger text-sm mt-1">
                    {entityIdError}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer border-t-0 flex justify-center gap-4 p-4 pt-0">
              <button
                type="button"
                className="flex btn btn-back w-[133px] h-[33px] !rounded-[8px] !items-center !justify-center !font-medium"
                onClick={handleBack}
              >
                Cancel
              </button>
              {(modalType === "modify" && modalType !== "view") ||
              !modalType ? (
                <button
                  type="submit"
                  className="flex btn btn-submit w-[133px] h-[33px] !rounded-[8px] !items-center !justify-center !font-medium"
                >
                  {loading ? (
                    <CircularProgress
                      className="circular-loader"
                      style={{ width: "20px", height: "20px" }}
                    />
                  ) : modalType === "modify" ? (
                    "Update"
                  ) : modalType === "view" ? null : (
                    "Add"
                  )}
                </button>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EntityModal;
